/**
 * SecureEmbed Management Console — Pricing Configuration
 *
 * Admin page for configuring per-portal pricing schemes. One schema, per-portal
 * values. Saved as a versioned DRAFT, then explicitly PUBLISHED to that portal's
 * checkout + entitlement gate. Stripe sync is an EXPLICIT button (never automatic
 * on save).
 *
 * Layout replicates documentation/pricing-config-mockup.html:
 *  - Portal selector tabs (each portal is fully independent)
 *  - Global settings (currency / GST display / cadence)
 *  - Freeform plan tiers (add / remove / reorder, entitlement chips, public/featured)
 *  - Seat classes
 *  - Metered usage + add-ons
 *  - Sector routes & modifiers
 *  - Live customer preview (right rail, sticky)
 *  - Version history (draft -> publish, with Restore)
 *  - Sync to Stripe (explicit)
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DollarSign,
  CreditCard,
  Plus,
  Trash2,
  Save,
  RefreshCw,
  Loader2,
  CheckCircle,
  ArrowUp,
  ArrowDown,
  Star,
  Eye,
  Upload,
  History,
} from 'lucide-react';
import {
  pricingApi,
  PricingConfig,
  PricingTier,
  PricingSeatClass,
  PricingMeteredItem,
  PricingAddon,
  PricingPortalSummary,
  PricingVersion,
  PricingSyncResult,
} from '../services/api';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const PORTAL_COLORS: Record<string, string> = {
  innovateflow: '#0EA5E9',
  food: '#16A34A',
  energy: '#F59E0B',
  theology: '#7C3AED',
  insurance: '#0EA5E9',
  education: '#2563EB',
  medicine: '#DC2626',
  tertiary: '#0891B2',
};

const newId = (): string =>
  `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

const currencySymbol = (currency: string): string => {
  if (currency === 'USD') return '$';
  if (currency === 'NZD') return 'NZ$';
  return 'A$';
};

const formatPrice = (price: number | string, currency: string): string => {
  if (typeof price === 'string') {
    const n = Number(price);
    if (price.trim() === '' || Number.isNaN(n)) return price; // e.g. "custom", "quoted"
    return currencySymbol(currency) + n.toLocaleString('en-AU');
  }
  if (price === 0) return currencySymbol(currency) + '0';
  return currencySymbol(currency) + price.toLocaleString('en-AU');
};

// Sensible default config so a portal with no saved pricing has a starting point.
const defaultConfig = (portalLabel: string): PricingConfig => ({
  portal_label: portalLabel,
  currency: 'AUD',
  gst_mode: 'b2c_inc_b2b_ex',
  cadence: 'monthly_annual',
  tiers: [
    {
      id: newId(),
      name: 'Free',
      price: 0,
      per: 'forever',
      gst: 'inclusive',
      inc: ['1 bundled seat'],
      pub: true,
      feat: false,
    },
    {
      id: newId(),
      name: 'Pro',
      price: 39,
      per: '/mo',
      gst: 'inclusive',
      inc: ['Everything in Free', 'Priority support'],
      pub: true,
      feat: true,
    },
  ],
  seat_classes: [{ class: 'Full seat', price: 0 }],
  metered: [],
  addons: [],
  sector_routes: {
    education_discount_pct: '',
    government_route: '',
    founding_coupon: '',
  },
});

const CURRENCY_OPTIONS = [
  { value: 'AUD', label: 'AUD (A$)' },
  { value: 'USD', label: 'USD ($)' },
  { value: 'NZD', label: 'NZD (NZ$)' },
];

const GST_MODE_OPTIONS = [
  { value: 'b2c_inc_b2b_ex', label: 'B2C inc-GST · B2B ex-GST' },
  { value: 'all_inclusive', label: 'All inclusive' },
  { value: 'all_exclusive', label: 'All exclusive' },
];

const CADENCE_OPTIONS = [
  { value: 'monthly_annual', label: 'Monthly + annual (2 mo free)' },
  { value: 'monthly_only', label: 'Monthly only' },
  { value: 'annual_only', label: 'Annual only' },
];

// ---------------------------------------------------------------------------
// Small reusable UI atoms (Tailwind iflow theme, light only)
// ---------------------------------------------------------------------------

const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <div className={`bg-white border border-gray-200 rounded-lg shadow-sm p-5 ${className}`}>
    {children}
  </div>
);

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="block text-[11px] font-bold uppercase tracking-wide text-gray-600 mb-1">
    {children}
  </label>
);

const inputCls =
  'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-900 focus:outline-none focus:border-iflow-500 focus:ring-2 focus:ring-iflow-100';

const Toggle: React.FC<{ on: boolean; onChange: () => void; title?: string }> = ({
  on,
  onChange,
  title,
}) => (
  <button
    type="button"
    title={title}
    onClick={onChange}
    className={`relative inline-block w-10 h-5 rounded-full transition-colors ${
      on ? 'bg-iflow-500' : 'bg-gray-300'
    }`}
  >
    <span
      className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${
        on ? 'right-0.5' : 'left-0.5'
      }`}
    />
  </button>
);

const IconBtn: React.FC<{
  onClick: () => void;
  title: string;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ onClick, title, disabled, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    disabled={disabled}
    className="w-7 h-7 inline-flex items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-iflow-700 disabled:opacity-30 disabled:cursor-not-allowed"
  >
    {children}
  </button>
);

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const PricingConfiguration: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const queryClient = useQueryClient();

  const [selectedPortal, setSelectedPortal] = useState<string>('');
  const [config, setConfig] = useState<PricingConfig | null>(null);
  const [dirty, setDirty] = useState(false);
  const [status, setStatus] = useState<'draft' | 'published'>('draft');
  const [version, setVersion] = useState<number | undefined>(undefined);
  const [syncResult, setSyncResult] = useState<PricingSyncResult | null>(null);
  const [newChip, setNewChip] = useState<Record<string, string>>({});

  // -- Portal list -------------------------------------------------------------
  const listQuery = useQuery({
    queryKey: ['pricing-portals'],
    queryFn: () => pricingApi.list(),
  });

  const portals: PricingPortalSummary[] = useMemo(
    () => listQuery.data?.portals ?? [],
    [listQuery.data]
  );

  // Default to first portal once the list loads.
  useEffect(() => {
    if (!selectedPortal && portals.length > 0) {
      setSelectedPortal(portals[0].portal_id);
    }
  }, [portals, selectedPortal]);

  // -- Selected portal config --------------------------------------------------
  const configQuery = useQuery({
    queryKey: ['pricing-config', selectedPortal],
    queryFn: () => pricingApi.get(selectedPortal),
    enabled: !!selectedPortal,
  });

  const versionsQuery = useQuery({
    queryKey: ['pricing-versions', selectedPortal],
    queryFn: () => pricingApi.versions(selectedPortal),
    enabled: !!selectedPortal,
  });

  // Populate editor from draft (fallback published, fallback defaults).
  useEffect(() => {
    if (!selectedPortal) return;
    if (configQuery.data) {
      const portalMeta = portals.find((p) => p.portal_id === selectedPortal);
      const label =
        portalMeta?.portal_label ||
        configQuery.data.draft?.portal_label ||
        configQuery.data.published?.portal_label ||
        selectedPortal;
      const loaded = configQuery.data.draft || configQuery.data.published;
      setConfig(loaded ? { ...loaded, portal_label: loaded.portal_label || label } : defaultConfig(label));
      setStatus(configQuery.data.draft ? 'draft' : configQuery.data.status || 'published');
      setVersion(
        configQuery.data.draft
          ? configQuery.data.draft_version
          : configQuery.data.published_version
      );
      setDirty(false);
      setSyncResult(null);
    }
  }, [configQuery.data, selectedPortal, portals]);

  // -- Mutations ---------------------------------------------------------------
  const saveMutation = useMutation({
    mutationFn: (cfg: PricingConfig) => pricingApi.saveDraft(selectedPortal, cfg),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-config', selectedPortal] });
      queryClient.invalidateQueries({ queryKey: ['pricing-versions', selectedPortal] });
      queryClient.invalidateQueries({ queryKey: ['pricing-portals'] });
      setDirty(false);
      setStatus('draft');
    },
  });

  const publishMutation = useMutation({
    mutationFn: () => pricingApi.publish(selectedPortal),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-config', selectedPortal] });
      queryClient.invalidateQueries({ queryKey: ['pricing-versions', selectedPortal] });
      queryClient.invalidateQueries({ queryKey: ['pricing-portals'] });
      setStatus('published');
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (v: number) => pricingApi.restore(selectedPortal, v),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-config', selectedPortal] });
      queryClient.invalidateQueries({ queryKey: ['pricing-versions', selectedPortal] });
    },
  });

  const syncMutation = useMutation({
    mutationFn: () => pricingApi.syncStripe(selectedPortal),
    onSuccess: (result) => {
      setSyncResult(result);
    },
  });

  // -- Local config mutators ---------------------------------------------------
  const patchConfig = (patch: Partial<PricingConfig>) => {
    setConfig((prev) => (prev ? { ...prev, ...patch } : prev));
    setDirty(true);
  };

  const patchSectorRoutes = (patch: Partial<PricingConfig['sector_routes']>) => {
    setConfig((prev) =>
      prev ? { ...prev, sector_routes: { ...prev.sector_routes, ...patch } } : prev
    );
    setDirty(true);
  };

  const updateTier = (index: number, patch: Partial<PricingTier>) => {
    setConfig((prev) => {
      if (!prev) return prev;
      const tiers = prev.tiers.map((t, i) => (i === index ? { ...t, ...patch } : t));
      return { ...prev, tiers };
    });
    setDirty(true);
  };

  const addTier = () => {
    setConfig((prev) => {
      if (!prev) return prev;
      const tier: PricingTier = {
        id: newId(),
        name: 'New tier',
        price: 0,
        per: '/mo',
        gst: 'exclusive',
        inc: [],
        pub: true,
        feat: false,
      };
      return { ...prev, tiers: [...prev.tiers, tier] };
    });
    setDirty(true);
  };

  const removeTier = (index: number) => {
    setConfig((prev) =>
      prev ? { ...prev, tiers: prev.tiers.filter((_, i) => i !== index) } : prev
    );
    setDirty(true);
  };

  const moveTier = (index: number, dir: -1 | 1) => {
    setConfig((prev) => {
      if (!prev) return prev;
      const target = index + dir;
      if (target < 0 || target >= prev.tiers.length) return prev;
      const tiers = [...prev.tiers];
      [tiers[index], tiers[target]] = [tiers[target], tiers[index]];
      return { ...prev, tiers };
    });
    setDirty(true);
  };

  const addChip = (index: number) => {
    const chip = (newChip[index] || '').trim();
    if (!chip) return;
    setConfig((prev) => {
      if (!prev) return prev;
      const tiers = prev.tiers.map((t, i) =>
        i === index ? { ...t, inc: [...t.inc, chip] } : t
      );
      return { ...prev, tiers };
    });
    setNewChip((prev) => ({ ...prev, [index]: '' }));
    setDirty(true);
  };

  const removeChip = (tierIndex: number, chipIndex: number) => {
    setConfig((prev) => {
      if (!prev) return prev;
      const tiers = prev.tiers.map((t, i) =>
        i === tierIndex ? { ...t, inc: t.inc.filter((_, c) => c !== chipIndex) } : t
      );
      return { ...prev, tiers };
    });
    setDirty(true);
  };

  // Seat classes
  const updateSeat = (index: number, patch: Partial<PricingSeatClass>) => {
    setConfig((prev) => {
      if (!prev) return prev;
      const seat_classes = prev.seat_classes.map((s, i) =>
        i === index ? { ...s, ...patch } : s
      );
      return { ...prev, seat_classes };
    });
    setDirty(true);
  };
  const addSeat = () => {
    setConfig((prev) =>
      prev
        ? { ...prev, seat_classes: [...prev.seat_classes, { class: 'New seat class', price: 0 }] }
        : prev
    );
    setDirty(true);
  };
  const removeSeat = (index: number) => {
    setConfig((prev) =>
      prev ? { ...prev, seat_classes: prev.seat_classes.filter((_, i) => i !== index) } : prev
    );
    setDirty(true);
  };

  // Metered
  const updateMetered = (index: number, patch: Partial<PricingMeteredItem>) => {
    setConfig((prev) => {
      if (!prev) return prev;
      const metered = prev.metered.map((m, i) => (i === index ? { ...m, ...patch } : m));
      return { ...prev, metered };
    });
    setDirty(true);
  };
  const addMetered = () => {
    setConfig((prev) =>
      prev
        ? {
            ...prev,
            metered: [
              ...prev.metered,
              { key: `m_${Date.now().toString(36)}`, label: 'New metered item', price: 0, unit: 'per unit' },
            ],
          }
        : prev
    );
    setDirty(true);
  };
  const removeMetered = (index: number) => {
    setConfig((prev) =>
      prev ? { ...prev, metered: prev.metered.filter((_, i) => i !== index) } : prev
    );
    setDirty(true);
  };

  // Add-ons
  const updateAddon = (index: number, patch: Partial<PricingAddon>) => {
    setConfig((prev) => {
      if (!prev) return prev;
      const addons = prev.addons.map((a, i) => (i === index ? { ...a, ...patch } : a));
      return { ...prev, addons };
    });
    setDirty(true);
  };
  const addAddon = () => {
    setConfig((prev) =>
      prev
        ? {
            ...prev,
            addons: [
              ...prev.addons,
              { key: `a_${Date.now().toString(36)}`, label: 'New add-on', price: 0, unit: 'per user / mo' },
            ],
          }
        : prev
    );
    setDirty(true);
  };
  const removeAddon = (index: number) => {
    setConfig((prev) =>
      prev ? { ...prev, addons: prev.addons.filter((_, i) => i !== index) } : prev
    );
    setDirty(true);
  };

  const handleSaveDraft = () => {
    if (config) saveMutation.mutate(config);
  };

  // ---------------------------------------------------------------------------
  // Render guards
  // ---------------------------------------------------------------------------

  if (listQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // Degrade gracefully on a 403 (ProtectedRoute already guards auth/admin).
  const listForbidden = (listQuery.error as { response?: { status?: number } } | undefined)
    ?.response?.status === 403;
  if (listQuery.isError && listForbidden) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <DollarSign className="w-10 h-10 text-gray-300 mx-auto mb-3" />
        <h2 className="text-lg font-semibold text-gray-700">Pricing Configuration</h2>
        <p className="text-sm text-gray-500 mt-2">
          You do not have permission to manage pricing. Ask a platform administrator for access.
        </p>
      </div>
    );
  }

  const currency = config?.currency || 'AUD';
  const sym = currencySymbol(currency);
  const publicTiers = config?.tiers.filter((t) => t.pub) ?? [];

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="max-w-[1280px] mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        {!embedded && (
          <div>
            <div className="flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-iflow-600" />
              <h1 className="text-2xl font-extrabold tracking-tight text-iflow-900">
                Pricing Configuration
              </h1>
            </div>
            <p className="text-sm text-gray-500 mt-1 max-w-3xl">
              Set the pricing scheme for each portal <b>independently</b>. One schema, per-portal
              values. Saved as a versioned draft, then published to that portal's checkout +
              entitlements.
            </p>
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
              status === 'draft'
                ? 'bg-amber-100 text-amber-800'
                : 'bg-emerald-100 text-emerald-700'
            }`}
          >
            {status === 'draft' ? 'Draft' : 'Published'}
            {version !== undefined ? ` · v${version}` : ''}
          </span>
          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={!config || !dirty || saveMutation.isPending}
            className="inline-flex items-center gap-2 border border-iflow-200 bg-white text-iflow-700 hover:bg-iflow-50 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {saveMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save draft
          </button>
          <button
            type="button"
            onClick={() => publishMutation.mutate()}
            disabled={!config || dirty || publishMutation.isPending}
            title={dirty ? 'Save the draft before publishing' : 'Publish this draft to the portal'}
            className="inline-flex items-center gap-2 bg-iflow-700 text-white hover:bg-iflow-800 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {publishMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            Publish to portal
          </button>
        </div>
      </div>

      {/* Status banners */}
      {saveMutation.isSuccess && !dirty && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-700 flex items-center gap-2">
          <CheckCircle className="w-4 h-4" /> Draft saved.
        </div>
      )}
      {publishMutation.isSuccess && status === 'published' && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-700 flex items-center gap-2">
          <CheckCircle className="w-4 h-4" /> Published to the portal's checkout + entitlement gate.
        </div>
      )}
      {(saveMutation.isError || publishMutation.isError || restoreMutation.isError) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          Something went wrong saving the change. Please retry.
        </div>
      )}

      {/* Portal selector */}
      <div className="flex items-center gap-2 flex-wrap bg-white border border-gray-200 rounded-lg shadow-sm px-3 py-2.5">
        <span className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mr-1">
          Portal
        </span>
        {portals.map((p) => {
          const active = p.portal_id === selectedPortal;
          const color = p.color || PORTAL_COLORS[p.portal_id] || '#0EA5E9';
          return (
            <button
              key={p.portal_id}
              type="button"
              onClick={() => setSelectedPortal(p.portal_id)}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold border ${
                active
                  ? 'border-iflow-500 bg-iflow-50 text-iflow-800 ring-1 ring-iflow-500'
                  : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <span className="w-2 h-2 rounded-sm" style={{ background: color }} />
              {p.portal_label || p.portal_id}
            </button>
          );
        })}
        {portals.length === 0 && (
          <span className="text-sm text-gray-400">No portals available.</span>
        )}
      </div>

      <div className="bg-iflow-50 border border-iflow-200 rounded-lg p-3 text-sm text-iflow-900">
        Switching portals here loads that portal's <b>own</b> independent pricing into the same
        editor — the management hub is the single place every portal's scheme is configured.
      </div>

      {/* Body */}
      {configQuery.isLoading || !config ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-4">
          {/* LEFT: editor */}
          <div className="space-y-4 min-w-0">
            {/* Global settings */}
            <Card>
              <h3 className="text-sm font-bold text-iflow-900">
                {config.portal_label} · global settings
              </h3>
              <p className="text-xs text-gray-500 mb-3">
                Currency, tax display and billing cadence for this portal.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <FieldLabel>Currency</FieldLabel>
                  <select
                    className={inputCls}
                    value={config.currency}
                    onChange={(e) => patchConfig({ currency: e.target.value })}
                  >
                    {CURRENCY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <FieldLabel>GST display</FieldLabel>
                  <select
                    className={inputCls}
                    value={config.gst_mode}
                    onChange={(e) => patchConfig({ gst_mode: e.target.value })}
                  >
                    {GST_MODE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <FieldLabel>Billing cadence</FieldLabel>
                  <select
                    className={inputCls}
                    value={config.cadence}
                    onChange={(e) => patchConfig({ cadence: e.target.value })}
                  >
                    {CADENCE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                Consumer (B2C) tiers are shown inc-GST per ACCC s48; business tiers quoted ex-GST.
                Set per tier below.
              </div>
            </Card>

            {/* Plan tiers */}
            <Card>
              <div className="flex items-center mb-3">
                <div>
                  <h3 className="text-sm font-bold text-iflow-900">Plan tiers</h3>
                  <p className="text-xs text-gray-500">
                    Reorder with the arrows. Mark public tiers and the featured plan.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addTier}
                  className="ml-auto inline-flex items-center gap-1.5 border border-iflow-200 bg-white text-iflow-700 hover:bg-iflow-50 px-3 py-1.5 rounded-lg text-sm font-medium"
                >
                  <Plus className="w-4 h-4" /> Add tier
                </button>
              </div>

              <div className="space-y-3">
                {config.tiers.map((tier, index) => (
                  <div
                    key={tier.id}
                    className={`border rounded-lg p-3.5 bg-gray-50/60 ${
                      tier.feat ? 'border-iflow-400 ring-1 ring-iflow-300' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <input
                        className={`${inputCls} max-w-[200px] font-bold`}
                        value={tier.name}
                        onChange={(e) => updateTier(index, { name: e.target.value })}
                      />
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
                          tier.gst === 'inclusive'
                            ? 'bg-iflow-50 text-iflow-700 border-iflow-100'
                            : 'bg-gray-100 text-gray-600 border-gray-200'
                        }`}
                      >
                        {tier.gst === 'inclusive' ? 'B2C · inc-GST' : 'B2B · ex-GST'}
                      </span>
                      {tier.feat && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-iflow-50 text-iflow-700 border border-iflow-100">
                          <Star className="w-3 h-3" /> featured
                        </span>
                      )}
                      <div className="ml-auto flex items-center gap-1.5">
                        <span className="text-[11px] text-gray-500 mr-1">Public</span>
                        <Toggle
                          on={tier.pub}
                          onChange={() => updateTier(index, { pub: !tier.pub })}
                          title="Public"
                        />
                        <IconBtn
                          onClick={() => updateTier(index, { feat: !tier.feat })}
                          title={tier.feat ? 'Unfeature' : 'Mark as featured'}
                        >
                          <Star
                            className={`w-3.5 h-3.5 ${tier.feat ? 'fill-iflow-500 text-iflow-500' : ''}`}
                          />
                        </IconBtn>
                        <IconBtn
                          onClick={() => moveTier(index, -1)}
                          title="Move up"
                          disabled={index === 0}
                        >
                          <ArrowUp className="w-3.5 h-3.5" />
                        </IconBtn>
                        <IconBtn
                          onClick={() => moveTier(index, 1)}
                          title="Move down"
                          disabled={index === config.tiers.length - 1}
                        >
                          <ArrowDown className="w-3.5 h-3.5" />
                        </IconBtn>
                        <IconBtn onClick={() => removeTier(index)} title="Remove tier">
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </IconBtn>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                      <div>
                        <FieldLabel>Price ({sym})</FieldLabel>
                        <input
                          className={inputCls}
                          value={String(tier.price)}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const num = Number(raw);
                            updateTier(index, {
                              price: raw.trim() !== '' && !Number.isNaN(num) ? num : raw,
                            });
                          }}
                        />
                      </div>
                      <div>
                        <FieldLabel>Interval</FieldLabel>
                        <input
                          className={inputCls}
                          value={tier.per}
                          onChange={(e) => updateTier(index, { per: e.target.value })}
                        />
                      </div>
                      <div>
                        <FieldLabel>GST</FieldLabel>
                        <select
                          className={inputCls}
                          value={tier.gst}
                          onChange={(e) =>
                            updateTier(index, {
                              gst: e.target.value as 'inclusive' | 'exclusive',
                            })
                          }
                        >
                          <option value="inclusive">inclusive</option>
                          <option value="exclusive">exclusive</option>
                        </select>
                      </div>
                      <div className="flex items-end">
                        <div className="text-[11px] text-gray-500">
                          {typeof tier.price === 'number' && tier.price > 0
                            ? 'Annual: 2 mo free'
                            : 'Annual: —'}
                        </div>
                      </div>
                    </div>

                    {/* Entitlement chips */}
                    <div className="mt-3 flex flex-wrap gap-1.5 items-center">
                      {tier.inc.map((chip, ci) => (
                        <span
                          key={ci}
                          className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 border border-gray-200"
                        >
                          {chip}
                          <button
                            type="button"
                            onClick={() => removeChip(index, ci)}
                            className="text-gray-400 hover:text-red-500"
                            title="Remove entitlement"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      <input
                        className="text-[11px] px-2.5 py-1 rounded-full border border-iflow-200 bg-white text-iflow-700 focus:outline-none focus:ring-1 focus:ring-iflow-300 w-40"
                        placeholder="+ entitlement"
                        value={newChip[index] || ''}
                        onChange={(e) =>
                          setNewChip((prev) => ({ ...prev, [index]: e.target.value }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addChip(index);
                          }
                        }}
                      />
                    </div>
                  </div>
                ))}
                {config.tiers.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-4">
                    No tiers yet. Click "Add tier" to create one.
                  </p>
                )}
              </div>
            </Card>

            {/* Seat classes */}
            <Card>
              <div className="flex items-center mb-3">
                <div>
                  <h3 className="text-sm font-bold text-iflow-900">Seat classes</h3>
                  <p className="text-xs text-gray-500">
                    Per-seat add-on pricing beyond a tier's included seats.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addSeat}
                  className="ml-auto inline-flex items-center gap-1.5 border border-iflow-200 bg-white text-iflow-700 hover:bg-iflow-50 px-3 py-1.5 rounded-lg text-sm font-medium"
                >
                  <Plus className="w-4 h-4" /> Add seat class
                </button>
              </div>
              <div className="divide-y divide-gray-100">
                {config.seat_classes.map((seat, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-[1.6fr_1fr_auto] gap-3 items-center py-2 hover:bg-gray-50 rounded"
                  >
                    <input
                      className={inputCls}
                      value={seat.class}
                      onChange={(e) => updateSeat(index, { class: e.target.value })}
                    />
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-gray-400">{sym}</span>
                      <input
                        className={inputCls}
                        value={String(seat.price)}
                        onChange={(e) => {
                          const num = Number(e.target.value);
                          updateSeat(index, {
                            price:
                              e.target.value.trim() !== '' && !Number.isNaN(num)
                                ? num
                                : e.target.value,
                          });
                        }}
                      />
                    </div>
                    <IconBtn onClick={() => removeSeat(index)} title="Remove seat class">
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </IconBtn>
                  </div>
                ))}
                {config.seat_classes.length === 0 && (
                  <p className="text-sm text-gray-400 py-3">No seat classes configured.</p>
                )}
              </div>
            </Card>

            {/* Metered + add-ons */}
            <Card>
              <h3 className="text-sm font-bold text-iflow-900">Metered usage &amp; add-ons</h3>
              <p className="text-xs text-gray-500 mb-3">
                Consumption-priced items and optional add-ons (e.g. SSO/Identity).
              </p>

              {/* Metered */}
              <div className="flex items-center mb-2">
                <span className="text-[11px] font-bold uppercase tracking-wide text-gray-500">
                  Metered usage
                </span>
                <button
                  type="button"
                  onClick={addMetered}
                  className="ml-auto inline-flex items-center gap-1 text-iflow-700 hover:text-iflow-800 text-xs font-medium"
                >
                  <Plus className="w-3.5 h-3.5" /> Add metered
                </button>
              </div>
              <div className="divide-y divide-gray-100 mb-4">
                {config.metered.map((m, index) => (
                  <div
                    key={m.key}
                    className="grid grid-cols-[1.4fr_1fr_1fr_auto] gap-2 items-center py-2 hover:bg-gray-50 rounded"
                  >
                    <input
                      className={inputCls}
                      value={m.label}
                      placeholder="Label"
                      onChange={(e) => updateMetered(index, { label: e.target.value })}
                    />
                    <input
                      className={inputCls}
                      value={String(m.price)}
                      placeholder="Price"
                      onChange={(e) => {
                        const num = Number(e.target.value);
                        updateMetered(index, {
                          price:
                            e.target.value.trim() !== '' && !Number.isNaN(num)
                              ? num
                              : e.target.value,
                        });
                      }}
                    />
                    <input
                      className={inputCls}
                      value={m.unit}
                      placeholder="Unit"
                      onChange={(e) => updateMetered(index, { unit: e.target.value })}
                    />
                    <IconBtn onClick={() => removeMetered(index)} title="Remove metered item">
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </IconBtn>
                  </div>
                ))}
                {config.metered.length === 0 && (
                  <p className="text-sm text-gray-400 py-3">No metered items.</p>
                )}
              </div>

              {/* Add-ons */}
              <div className="flex items-center mb-2">
                <span className="text-[11px] font-bold uppercase tracking-wide text-gray-500">
                  Add-ons
                </span>
                <button
                  type="button"
                  onClick={addAddon}
                  className="ml-auto inline-flex items-center gap-1 text-iflow-700 hover:text-iflow-800 text-xs font-medium"
                >
                  <Plus className="w-3.5 h-3.5" /> Add add-on
                </button>
              </div>
              <div className="divide-y divide-gray-100">
                {config.addons.map((a, index) => (
                  <div
                    key={a.key}
                    className="grid grid-cols-[1.4fr_1fr_1fr_auto] gap-2 items-center py-2 hover:bg-gray-50 rounded"
                  >
                    <input
                      className={inputCls}
                      value={a.label}
                      placeholder="Label"
                      onChange={(e) => updateAddon(index, { label: e.target.value })}
                    />
                    <input
                      className={inputCls}
                      value={String(a.price)}
                      placeholder="Price"
                      onChange={(e) => {
                        const num = Number(e.target.value);
                        updateAddon(index, {
                          price:
                            e.target.value.trim() !== '' && !Number.isNaN(num)
                              ? num
                              : e.target.value,
                        });
                      }}
                    />
                    <input
                      className={inputCls}
                      value={a.unit}
                      placeholder="Unit"
                      onChange={(e) => updateAddon(index, { unit: e.target.value })}
                    />
                    <IconBtn onClick={() => removeAddon(index)} title="Remove add-on">
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </IconBtn>
                  </div>
                ))}
                {config.addons.length === 0 && (
                  <p className="text-sm text-gray-400 py-3">No add-ons.</p>
                )}
              </div>
            </Card>

            {/* Sector routes */}
            <Card>
              <h3 className="text-sm font-bold text-iflow-900">Sector routes &amp; modifiers</h3>
              <p className="text-xs text-gray-500 mb-3">
                Discounts and procurement routes that apply on top of the tiers.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <FieldLabel>Education discount</FieldLabel>
                  <input
                    className={inputCls}
                    value={config.sector_routes.education_discount_pct}
                    placeholder="e.g. 30%"
                    onChange={(e) =>
                      patchSectorRoutes({ education_discount_pct: e.target.value })
                    }
                  />
                </div>
                <div>
                  <FieldLabel>Government route</FieldLabel>
                  <input
                    className={inputCls}
                    value={config.sector_routes.government_route}
                    placeholder="e.g. BuyICT / NSW SCM0020 (RFQ)"
                    onChange={(e) => patchSectorRoutes({ government_route: e.target.value })}
                  />
                </div>
                <div>
                  <FieldLabel>Founding coupon</FieldLabel>
                  <input
                    className={inputCls}
                    value={config.sector_routes.founding_coupon}
                    placeholder="e.g. 50% off year 1"
                    onChange={(e) => patchSectorRoutes({ founding_coupon: e.target.value })}
                  />
                </div>
              </div>
            </Card>
          </div>

          {/* RIGHT: preview + meta */}
          <div className="space-y-4">
            <div className="xl:sticky xl:top-4 space-y-4">
              {/* Live customer preview */}
              <Card className="bg-gradient-to-b from-white to-iflow-50/40">
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-3">
                  <Eye className="w-3.5 h-3.5 text-iflow-600" /> Live customer preview ·{' '}
                  <span className="text-iflow-700">{config.portal_label}</span>
                </div>
                <div className="space-y-2.5">
                  {publicTiers.map((tier) => (
                    <div
                      key={tier.id}
                      className={`border rounded-lg p-3 bg-white ${
                        tier.feat ? 'border-iflow-500 shadow-md shadow-iflow-100' : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-[13px] text-iflow-900">{tier.name}</span>
                        {tier.feat && (
                          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-iflow-50 text-iflow-700 border border-iflow-100">
                            Most popular
                          </span>
                        )}
                      </div>
                      <div className="mt-1">
                        <span className="text-2xl font-extrabold text-gray-900">
                          {formatPrice(tier.price, currency)}
                        </span>
                        <span className="text-[11px] text-gray-500"> {tier.per}</span>
                      </div>
                      <ul className="mt-2 space-y-1">
                        {tier.inc.slice(0, 4).map((i, ci) => (
                          <li key={ci} className="text-[11.5px] text-gray-600 pl-4 relative">
                            <CheckCircle className="w-3 h-3 text-iflow-500 absolute left-0 top-0.5" />
                            {i}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                  {publicTiers.length === 0 && (
                    <p className="text-xs text-gray-400">
                      No public tiers — toggle a tier "Public" to preview it.
                    </p>
                  )}
                </div>
                <p className="mt-3 text-[11px] text-gray-400">
                  Updates as you edit. This is how the tier cards render on the portal's pricing page.
                </p>
              </Card>

              {/* Sync to Stripe */}
              <Card>
                <div className="flex items-center gap-2 mb-1">
                  <CreditCard className="w-4 h-4 text-iflow-600" />
                  <h3 className="text-sm font-bold text-iflow-900">Sync to Stripe</h3>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  Explicitly push the published config to Stripe products/prices. This is not
                  automatic on save.
                </p>
                <button
                  type="button"
                  onClick={() => syncMutation.mutate()}
                  disabled={!selectedPortal || syncMutation.isPending}
                  className="w-full inline-flex items-center justify-center gap-2 bg-iflow-700 text-white hover:bg-iflow-800 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                >
                  {syncMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Sync to Stripe
                </button>
                {syncMutation.isError && (
                  <p className="mt-2 text-xs text-red-600">Stripe sync failed. Please retry.</p>
                )}
                {syncResult && (
                  <div className="mt-3 bg-iflow-50 border border-iflow-200 rounded-lg p-3 text-xs text-iflow-900">
                    <div className="flex items-center gap-2 mb-1.5">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                      <b>Synced</b>
                      <span
                        className={`ml-auto px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          syncResult.mode === 'live'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {syncResult.mode === 'live' ? 'live mode' : 'test mode'}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <div className="text-base font-bold text-iflow-800">
                          {syncResult.created}
                        </div>
                        <div className="text-[10px] text-gray-500">created</div>
                      </div>
                      <div>
                        <div className="text-base font-bold text-iflow-800">
                          {syncResult.updated}
                        </div>
                        <div className="text-[10px] text-gray-500">updated</div>
                      </div>
                      <div>
                        <div className="text-base font-bold text-iflow-800">
                          {syncResult.skipped}
                        </div>
                        <div className="text-[10px] text-gray-500">skipped</div>
                      </div>
                    </div>
                    {syncResult.message && (
                      <p className="mt-2 text-[11px] text-gray-600">{syncResult.message}</p>
                    )}
                  </div>
                )}
              </Card>

              {/* Version history */}
              <Card>
                <div className="flex items-center gap-2 mb-2">
                  <History className="w-4 h-4 text-iflow-600" />
                  <h3 className="text-sm font-bold text-iflow-900">Version history</h3>
                </div>
                {versionsQuery.isLoading ? (
                  <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading versions…
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {(versionsQuery.data?.versions ?? []).map((v: PricingVersion) => (
                      <div key={v.version} className="flex items-center gap-2 py-2 text-xs">
                        <span className="w-6 h-6 rounded-full bg-iflow-100 text-iflow-700 flex items-center justify-center font-bold text-[11px]">
                          {(v.author || 'SC').slice(0, 2).toUpperCase()}
                        </span>
                        <div className="min-w-0">
                          <div className="font-semibold text-gray-800">
                            v{v.version}
                            {v.status === 'draft' ? ' · draft' : ''}
                            {v.status === 'published' ? ' · published' : ''}
                          </div>
                          <div className="text-[11px] text-gray-400 truncate">
                            {v.author || 'you'}
                            {v.created_at ? ` · ${v.created_at}` : ''}
                            {v.note ? ` · ${v.note}` : ''}
                          </div>
                        </div>
                        <div className="ml-auto">
                          {v.status === 'draft' && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800">
                              draft
                            </span>
                          )}
                          {v.status === 'published' && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700">
                              live
                            </span>
                          )}
                          {v.status === 'archived' && (
                            <button
                              type="button"
                              onClick={() => restoreMutation.mutate(v.version)}
                              disabled={restoreMutation.isPending}
                              className="px-2 py-0.5 rounded-md text-[10px] font-medium border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                            >
                              {restoreMutation.isPending ? 'Restoring…' : 'Restore'}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    {(versionsQuery.data?.versions ?? []).length === 0 && (
                      <p className="text-xs text-gray-400 py-2">
                        No versions yet — save a draft to create v1.
                      </p>
                    )}
                  </div>
                )}
                <p className="mt-2 text-[11px] text-gray-400">
                  Publish writes the live config the portal's checkout + entitlement gate read.
                  Drafts never affect customers.
                </p>
              </Card>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingConfiguration;
